import React from 'react';
import Image from 'next/image';
import debounce from 'lodash/debounce';
import Button from '@mui/material/Button';
import { Input } from '@/components/form';
import SubscribeModal from './SubscribeModal';
import SearchArea from './SearchArea';
import validators from '@/utils/validators';
import FormValidator from '@/utils/form-validator';
import { trackClickEvent } from '@/utils/site-track';
import { NATION_WIDE } from '@/constants/city';
import { SearchType } from '@/types/job-title';
import { Option } from '@/components/form/cascade-select/CascadeSelect';
import subscribeIcon from '@/assets/job-title/subscribe-icon.svg';

type Props = {
  isJobTitle?: boolean;
  initValue?: SearchType;
  dataEleId?: string;
  dataObserverId?: string;
};

const INPUT_TYPE_SEARCH_VALUE = 'search_value';
const INPUT_TYPE_EMAIL = 'email';

const VALIDATOR_MAP = {
  [INPUT_TYPE_SEARCH_VALUE]: [
    validators.valueRequired('请选择需要订阅的岗位或公司'),
  ],
  [INPUT_TYPE_EMAIL]: [
    validators.valueRequired('请输入你的邮箱'),
    validators.email(),
  ],
};

const INPUT_TYPE_LIST = Object.keys(VALIDATOR_MAP);

type Field = (typeof INPUT_TYPE_LIST)[number];
type FieldType = Record<Field, string>;

const formValidator = new FormValidator(VALIDATOR_MAP);

const SubscribeJob = (props: Props) => {
  const {
    isJobTitle = true,
    initValue = null,
    dataEleId = 'subscribe_job-title',
    dataObserverId = 'subscribe_job-title',
  } = props;

  const [open, setOpen] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [success, setSuccess] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState<SearchType>(initValue);
  const [cityValue, setCityValue] = React.useState<Option>(NATION_WIDE);
  const [error, setError] = React.useState<FieldType>({});

  const handleSearchValueChange = (val: SearchType) => {
    if (!val) return;
    setSearchValue(val);
    handleValidate({
      [INPUT_TYPE_SEARCH_VALUE]: val.id,
    });
  };

  const handleCityChange = (value: Option) => setCityValue(value);

  const handleEmailValidate = React.useCallback(
    debounce((value) => {
      handleValidate({
        [INPUT_TYPE_EMAIL]: value,
      });
    }, 200),
    [],
  );

  const handleEmailChange = (val: string) => {
    setEmail(val);
    handleEmailValidate(val);
  };

  const handleError = (errorMsgs: FieldType) => {
    setError((prev) => ({
      ...prev,
      ...errorMsgs,
    }));
  };

  const handleValidate = (values: FieldType) => {
    formValidator.validate(values);
    const error = formValidator.getError();
    error && handleError(error as FieldType);
    return formValidator.isOk();
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const isOk = handleValidate({
      [INPUT_TYPE_EMAIL]: email,
    });
    if (isOk) {
      trackClickEvent(e);
      setOpen(true);
    }
  };

  const handleSuccess = () => {
    setSuccess(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <div
        className={`bg-white rounded-lg p-5${isJobTitle ? ' mt-6' : ''}`}
        data-observer-id={dataObserverId}
      >
        <div
          className={
            isJobTitle
              ? 'flex gap-4 items-center'
              : 'flex flex-col gap-2 items-center'
          }
        >
          <Image
            alt=""
            src={subscribeIcon}
            className={isJobTitle ? '' : 'mx-auto'}
          />
          <div>
            <p
              className={`font-semibold text-black/85 ${isJobTitle ? 'text-2xl' : 'text-xl'}`}
            >
              20,861+ 岗位更新等你来订阅
            </p>
            <p className="text-sm leading-5.5 mt-2">
              一键订阅最新的岗位，每周送达
            </p>
          </div>
        </div>
        <div className={isJobTitle ? 'mt-5' : 'mt-8'}>
          <div className={success ? 'block' : 'hidden'}>
            <div
              className={`flex flex-col${isJobTitle ? '' : ' items-center'}`}
            >
              <p
                className={`my-5 font-semibold text-2xl ${isJobTitle ? 'text-left' : 'text-center'}`}
              >
                🎉恭喜你，订阅成功
              </p>
              <span
                className="underline text-web-blue-500 cursor-pointer"
                onClick={() => setSuccess(false)}
              >
                继续订阅
              </span>
            </div>
          </div>
          <div className={success ? 'hidden' : 'block'}>
            <div
              className={
                isJobTitle
                  ? 'grid grid-cols-1 gap-4 tablet:flex'
                  : 'grid grid-cols-1 gap-4'
              }
            >
              <SearchArea
                searchValue={searchValue}
                handeSearchValue={handleSearchValueChange}
                cityValue={cityValue}
                handleCitySearch={handleCityChange}
                initSearchValue={initValue}
              />
              <Input
                value={email}
                onChange={handleEmailChange}
                error={!!error[INPUT_TYPE_EMAIL]}
                helperText={error[INPUT_TYPE_EMAIL]}
                placeholder="输入你的邮箱地址"
                className="flex-1"
                type={INPUT_TYPE_EMAIL}
              />
              <div
                className={`flex-1 flex ${isJobTitle ? 'flex-col' : 'flex-col-reverse'}`}
              >
                <Button
                  variant="contained"
                  fullWidth
                  className="!rounded-lg !h-12"
                  onClick={handleClick}
                  data-ele-id={dataEleId}
                  data-ele-group="subscribe"
                >
                  订阅
                </Button>
                <p className="text-xs text-black/65 my-2 line-clamp-1">
                  您可以在邮箱中随时取消订阅
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SubscribeModal
        open={open}
        email={email}
        cityValue={cityValue}
        searchValue={searchValue}
        handleSuccess={handleSuccess}
        handleClose={handleClose}
      />
    </>
  );
};

export default SubscribeJob;
